// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import React from "react";
import { ApolloCache, FetchResult, gql } from "@apollo/client";
import { subsetTypeHasKey } from "@hex-insights/core";
import {
	graphTypeNames,
	Guardian,
	Relationship,
	RelationshipCreateInput,
	RelationshipCreateMutation,
	RelationshipDeleteMutation,
	RelationshipDetailDocument,
	RelationshipDetailQuery,
	RelationshipDetailQueryVariables,
	RelationshipUpdateInput,
	RelationshipUpdateMutation,
	Student,
	useRelationshipCreateMutation,
	useRelationshipDeleteMutation,
	useRelationshipUpdateMutation,
} from "../../../Utilities";
import * as RelationshipFormConversion from "./conversion";
import * as RelationshipFormValues from "./form-values";
import { customUpdateCacheForCreate, customUpdateCacheForDelete, customUpdateCacheForUpdate } from "./mutation-custom";

/**
 * Returns a `create` function for the Relationship model. The `create` function translates the given
 * `formValues` to the GraphQL create input for the Relationship model.
 */
export function useCreate<K extends keyof RelationshipFormValues.Create = keyof RelationshipFormValues.Create>() {
	const create = useDirectCreate();

	return React.useCallback(
		async (formValues: RelationshipFormValues.CreateSubset<K>) => {
			const input = RelationshipFormConversion.toGQLCreateInput(formValues);
			return create(input);
		},
		[create],
	);
}

/**
 * Returns a `create` function for the Relationship model. The `create` function accepts a GraphQL
 * create input for the model.
 */
export function useDirectCreate() {
	const [createRelationship] = useRelationshipCreateMutation();

	return React.useCallback(
		async (input: RelationshipCreateInput) => {
			const updateCache = getUpdateCacheForCreate(input);

			const { data, errors } = await createRelationship({ variables: { input }, update: updateCache });

			return { data: data?.createRelationship ?? null, errors: errors ?? null };
		},
		[createRelationship],
	);
}

/**
 * Returns an `update` function for the Relationship model. The `update` function translates the given
 * `formValues` to the GraphQL update input for the Relationship model.
 *
 * @param id The ID of the instance to update.
 */
export function useUpdate<K extends keyof RelationshipFormValues.Detail = keyof RelationshipFormValues.Detail>(
	id: Relationship["id"],
) {
	const update = useDirectUpdate<K>(id);

	return React.useCallback(
		async (
			changedFormValues: Partial<RelationshipFormValues.DetailSubset<K>>,
			initialFormValues: RelationshipFormValues.DetailSubset<K>,
		) => {
			const input = RelationshipFormConversion.toGQLUpdateInput(changedFormValues, initialFormValues);
			return update(input, initialFormValues);
		},
		[update],
	);
}

/**
 * Returns an `update` function for the Relationship model. The `update` function accepts a GraphQL
 * update input for the model. The `initialFormValues` argument of the returned function should include at least those
 * keys that will be updated by the mutation.
 *
 * @param id The ID of the instance to update.
 */
export function useDirectUpdate<K extends keyof RelationshipFormValues.Detail = keyof RelationshipFormValues.Detail>(
	id: Relationship["id"],
) {
	const [updateRelationship] = useRelationshipUpdateMutation();

	return React.useCallback(
		async (input: RelationshipUpdateInput, initialFormValues: RelationshipFormValues.DetailSubset<K>) => {
			const updateCache = getUpdateCacheForUpdate(input, initialFormValues);

			const { data, errors } = await updateRelationship({ variables: { id, input }, update: updateCache });

			return { data: data?.updateRelationship ?? null, errors: errors ?? null };
		},
		[updateRelationship, id],
	);
}

/**
 * Returns a `del` function for the Relationship model.
 *
 * @param id The ID of the instance to delete.
 */
export function useDelete(id: Relationship["id"]) {
	const [deleteRelationship] = useRelationshipDeleteMutation();

	return React.useCallback(async () => {
		const updateCache = getUpdateCacheForDelete(id);

		const { data, errors } = await deleteRelationship({ variables: { id }, update: updateCache });

		return { data: data?.deleteRelationship ?? false, errors: errors ?? null };
	}, [deleteRelationship, id]);
}

function getUpdateCacheForCreate(input: RelationshipCreateInput) {
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<RelationshipCreateMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		const createdObject = result.data?.createRelationship;
		if (!createdObject) {
			return;
		}

		cache.writeQuery<RelationshipDetailQuery, RelationshipDetailQueryVariables>({
			query: RelationshipDetailDocument,
			data: { relationship: createdObject },
			variables: { id: createdObject.id },
		});

		if (input.guardianID) {
			addToRelationshipsOfGuardianCache(cache, input.guardianID, createdObject);
		}

		if (input.studentID) {
			addToRelationshipsOfStudentCache(cache, input.studentID, createdObject);
		}

		cache.evict({ id: "ROOT_QUERY", fieldName: "relationshipConnection" });

		customUpdateCacheForCreate(input, cache, result);
	};
}

function getUpdateCacheForUpdate<K extends keyof RelationshipFormValues.Detail = keyof RelationshipFormValues.Detail>(
	input: RelationshipUpdateInput,
	initialFormValues: RelationshipFormValues.DetailSubset<K>,
) {
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<RelationshipUpdateMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		const updatedObject = result.data?.updateRelationship;
		if (!updatedObject) {
			return;
		}

		if (subsetTypeHasKey(initialFormValues, "guardianID") && initialFormValues.guardianID && input.guardianID) {
			removeFromRelationshipsOfGuardianCache(cache, initialFormValues.guardianID, updatedObject);
		}
		if (input.guardianID) {
			addToRelationshipsOfGuardianCache(cache, input.guardianID, updatedObject);
		}

		if (subsetTypeHasKey(initialFormValues, "studentID") && initialFormValues.studentID && input.studentID) {
			removeFromRelationshipsOfStudentCache(cache, initialFormValues.studentID, updatedObject);
		}
		if (input.studentID) {
			addToRelationshipsOfStudentCache(cache, input.studentID, updatedObject);
		}

		customUpdateCacheForUpdate(input, initialFormValues, cache, result);
	};
}

function getUpdateCacheForDelete(id: Relationship["id"]) {
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<RelationshipDeleteMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		if (!result.data?.deleteRelationship) {
			return;
		}

		cache.evict({ id: cache.identify({ id, __typename: graphTypeNames.Relationship }) });
		cache.evict({ id: "ROOT_QUERY", fieldName: "relationshipConnection" });
		cache.gc();

		customUpdateCacheForDelete(id, cache, result);
	};
}

function addToRelationshipsOfGuardianCache(
	cache: ApolloCache<any>,
	targetID: Guardian["id"],
	object: Pick<Relationship, "id">,
) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Guardian }),
		fields: {
			relationships: (cachedValue) => [...cachedValue, objectRef],
		},
	});
}

function removeFromRelationshipsOfGuardianCache(
	cache: ApolloCache<any>,
	targetID: Guardian["id"],
	object: Pick<Relationship, "id">,
) {
	const cacheID = cache.identify(object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Guardian }),
		fields: {
			relationships: (cachedValue) => cachedValue.filter((e: any) => e.__ref !== cacheID),
		},
	});
}

function addToRelationshipsOfStudentCache(
	cache: ApolloCache<any>,
	targetID: Student["id"],
	object: Pick<Relationship, "id">,
) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Student }),
		fields: {
			relationships: (cachedValue) => [...cachedValue, objectRef],
		},
	});
}

function removeFromRelationshipsOfStudentCache(
	cache: ApolloCache<any>,
	targetID: Student["id"],
	object: Pick<Relationship, "id">,
) {
	const cacheID = cache.identify(object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.Student }),
		fields: {
			relationships: (cachedValue) => cachedValue.filter((e: any) => e.__ref !== cacheID),
		},
	});
}

function toCacheRef(cache: ApolloCache<any>, object: Pick<Relationship, "id">) {
	return (
		cache.writeFragment({
			fragment: gql`
				fragment RelationshipRef on Relationship {
					id
				}
			`,
			data: object,
		}) ?? null
	);
}
